import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Save, Plus, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import MuiInput from "../components/MuiInput";
import { Card } from "../components/ui/card";
import { testrunService } from "../services/testrunService";
import { userService } from "../services/userService";
import { projectService } from "../services/projectService";
import { suiteService } from "../services/suiteService";
import { baselineService } from "../services/baselineService";
import { branchService } from "../services/branchService";
import { getStoredClientId } from "../utils/tokenUtils";
import SelectTestCasesModal from "../components/testrun/SelectTestCasesModal";
import toast from "react-hot-toast";
import MuiSelect from "../components/MuiSelect";
import { Skeleton } from "../components/common/Skeleton";

const NewTestRunPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "Project";

  const testRunId = searchParams.get("test_run_id");
  const mode = searchParams.get("mode");
  const isEditMode = useMemo(() => mode === "edit", [mode]);
  const isCloneMode = useMemo(() => mode === "clone", [mode]);

  const [formData, setFormData] = useState({
    name: "",
    status: "active",
    references: "",
    assigned_to: [],
    deadline: null,
    external_id: "",
    include_type: "all",
    description: "",
  });
  const [users, setUsers] = useState([]);
  const [showSelectTestCases, setShowSelectTestCases] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({});
  const deadlineInputRef = useRef(null);

  // Structure-specific state
  const [projectStructure, setProjectStructure] = useState(null);
  const [loadingProjectStructure, setLoadingProjectStructure] = useState(false);
  const [testPlans, setTestPlans] = useState([]);
  const [suites, setSuites] = useState([]);
  const [loadingSuites, setLoadingSuites] = useState(false);
  const [baselines, setBaselines] = useState([]);
  const [loadingBaselines, setLoadingBaselines] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Structure-specific form fields
  const [baselineId, setBaselineId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [suiteIds, setSuiteIds] = useState([]);
  const [testPlanId, setTestPlanId] = useState("");
  const [testPlanIds, setTestPlanIds] = useState([]);
  const [autoAssignTestCases, setAutoAssignTestCases] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await userService.findUsers({
        project_id: projectId,
      });
      if (response.success) {
        setUsers(response.data?.users || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [projectId]);

  // Load project structure
  const loadProjectStructure = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingProjectStructure(true);
      const clientId = getStoredClientId();
      const response = await projectService.getProjects({
        client_id: clientId,
      });
      const projects = response?.data?.projects || response?.projects || [];
      const project = projects.find((p) => p.project_id === projectId);
      const structure = project?.structure || "SINGLE_REPOSITORY";
      setProjectStructure(structure);
    } catch (error) {
      console.error("Error loading project structure:", error);
      setProjectStructure("SINGLE_REPOSITORY"); // Default fallback
    } finally {
      setLoadingProjectStructure(false);
    }
  }, [projectId]);

  // Load suites for MULTI_SUITE
  const loadSuites = useCallback(async () => {
    if (
      !projectId ||
      (projectStructure !== "MULTI_SUITE" &&
        projectStructure !== "MULTIPLE_TEST_SUITES")
    ) {
      setSuites([]);
      return;
    }
    try {
      setLoadingSuites(true);
      const clientId = getStoredClientId();
      const response = await suiteService.getSuites(projectId, clientId);
      const suitesList =
        response?.suites ??
        response?.data?.suites ??
        (Array.isArray(response?.data) ? response.data : []) ??
        [];
      setSuites(suitesList);
    } catch (error) {
      console.error("Error loading suites:", error);
      setSuites([]);
    } finally {
      setLoadingSuites(false);
    }
  }, [projectId, projectStructure]);

  // Load baselines for BASELINE_ENABLED
  const loadBaselines = useCallback(async () => {
    if (
      !projectId ||
      (projectStructure !== "BASELINE_ENABLED" &&
        projectStructure !== "SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH")
    ) {
      setBaselines([]);
      return;
    }
    try {
      setLoadingBaselines(true);
      const clientId = getStoredClientId();
      const response = await baselineService.getBaselines(projectId, clientId, {
        status: "published",
      });

      // Handle different response structures
      // Backend returns: { success: true, message: "...", data: [...baselines...] }
      let baselinesList = [];

      // Standard structure: { success: true, message: "...", data: [...] }
      if (response?.success && response?.data) {
        if (Array.isArray(response.data)) {
          baselinesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          baselinesList = response.data.data;
        } else if (
          response.data?.baselines &&
          Array.isArray(response.data.baselines)
        ) {
          baselinesList = response.data.baselines;
        }
      }
      // Check for structure: { message: { message: "...", data: [...] }, data: null }
      else if (
        response?.message?.data &&
        Array.isArray(response.message.data)
      ) {
        baselinesList = response.message.data;
      }
      // Check for structure: { data: [...] }
      else if (response?.data) {
        if (Array.isArray(response.data)) {
          baselinesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          baselinesList = response.data.data;
        } else if (
          response.data?.baselines &&
          Array.isArray(response.data.baselines)
        ) {
          baselinesList = response.data.baselines;
        }
      }
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        baselinesList = response;
      }

      console.log("NewTestRunPage - Baseline response:", response);
      console.log("NewTestRunPage - Loaded baselines:", baselinesList);
      setBaselines(baselinesList);
    } catch (error) {
      console.error("Error loading baselines:", error);
      setBaselines([]);
    } finally {
      setLoadingBaselines(false);
    }
  }, [projectId, projectStructure]);

  // Load branches for BASELINE_ENABLED
  const loadBranches = useCallback(async () => {
    if (
      !projectId ||
      (projectStructure !== "BASELINE_ENABLED" &&
        projectStructure !== "SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH")
    ) {
      setBranches([]);
      return;
    }
    try {
      setLoadingBranches(true);
      const clientId = getStoredClientId();
      const response = await branchService.getBranches(
        projectId,
        clientId,
        null,
        { status: "active" },
      );

      // Handle different response structures
      // Backend returns: { success: true, message: "...", data: [...branches...] }
      let branchesList = [];

      // Standard structure: { success: true, message: "...", data: [...] }
      if (response?.success && response?.data) {
        if (Array.isArray(response.data)) {
          branchesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          branchesList = response.data.data;
        } else if (
          response.data?.branches &&
          Array.isArray(response.data.branches)
        ) {
          branchesList = response.data.branches;
        }
      }
      // Check for structure: { message: { message: "...", data: [...] }, data: null }
      else if (
        response?.message?.data &&
        Array.isArray(response.message.data)
      ) {
        branchesList = response.message.data;
      }
      // Check for structure: { data: [...] }
      else if (response?.data) {
        if (Array.isArray(response.data)) {
          branchesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          branchesList = response.data.data;
        } else if (
          response.data?.branches &&
          Array.isArray(response.data.branches)
        ) {
          branchesList = response.data.branches;
        }
      }
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        branchesList = response;
      }

      console.log("NewTestRunPage - Branch response:", response);
      console.log("NewTestRunPage - Loaded branches:", branchesList);
      setBranches(branchesList);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  }, [projectId, projectStructure]);

  // Load clone data if cloneId is provided
  useEffect(() => {
    const loadTestRunData = async () => {
      if (!testRunId || !projectId) return;
      try {
        const response = await testrunService.getTestRun(testRunId, projectId);
        if (response.success) {
          const data = response.data;
          let deadline = data.deadline;
          if (deadline && typeof deadline === "string") {
            deadline = deadline.split("T")[0];
          }
          let name = data.name;
          if (isCloneMode) {
            name = `${data.name || ""} (Copy)`;
          }
          const rawAssigned = data.assigned_to;
          let assignedTo = [];
          if (rawAssigned == null || rawAssigned === "") assignedTo = [];
          else if (Array.isArray(rawAssigned))
            assignedTo = rawAssigned.map(String);
          else if (
            typeof rawAssigned === "string" &&
            rawAssigned.includes(",")
          ) {
            assignedTo = rawAssigned
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
          } else assignedTo = [String(rawAssigned)];
          setFormData({
            name: name,
            status: data.status || "active",
            references: data.references || "",
            assigned_to: assignedTo,
            deadline: deadline || null,
            external_id: "",
            include_type: data.include_type || "all",
            description: data.description || "",
            filter_criteria: data.filter_criteria || {},
          });
          setFilterCriteria(data.filter_criteria || {});
          setBaselineId(data.baseline_id || "");
          setBranchId(data.branch_id || "");
          setSuiteIds(data.suite_ids || []);

          if (
            data.include_type === "specific" &&
            data.test_case_ids &&
            data.test_case_ids.length > 0
          ) {
            setSelectedTestCases(
              data.test_case_ids.map((id) => ({ test_case_id: id })),
            );
          }
        }
      } catch (error) {
        console.error("Error loading clone data:", error);
        toast.error("Failed to load test run data for cloning");
      }
    };

    loadTestRunData();
  }, [testRunId, projectId]);

  useEffect(() => {
    if (projectId) {
      loadUsers();
      loadProjectStructure();
    }
  }, [projectId, loadUsers, loadProjectStructure]);

  // Load structure-specific data when structure is known
  useEffect(() => {
    if (projectStructure) {
      loadSuites();
      loadBaselines();
      loadBranches();
    }
  }, [projectStructure, loadSuites, loadBaselines, loadBranches]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleIncludeTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, include_type: value }));
    if (value === "all") {
      setSelectedTestCases([]);
      setFilterCriteria({});
    } else if (value === "filter") {
      setFormData((prev) => ({
        ...prev,
        filter_criteria: filterCriteria,
      }));
    }
  };

  const handleTestCasesSelected = (data) => {
    if (formData.include_type === "filter") {
      const criteria = data.filter_criteria || {};
      setFilterCriteria(criteria);
      setFormData((prev) => ({ ...prev, filter_criteria: criteria }));
    } else {
      setSelectedTestCases(Array.isArray(data) ? data : []);
    }
    setShowSelectTestCases(false);
  };

  const handleSave = async () => {
    // console.log("handleSave", formData);
    // debugger;
    if (!formData.name.trim()) {
      toast.error("Test Run Name is required");
      return;
    }

    if (
      !Array.isArray(formData.assigned_to) ||
      formData.assigned_to.length === 0
    ) {
      toast.error("Assign at least one user");
      return;
    }

    // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
    // if (projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') {
    //   if (!baselineId && !branchId) {
    //     toast.error('Please select either a Baseline or Branch for this project type');
    //     return;
    //   }
    // }

    if (
      formData.include_type === "specific" &&
      selectedTestCases.length === 0
    ) {
      toast.error("Please select at least one test case");
      return;
    }

    if (
      formData.include_type === "filter" &&
      Object.keys(filterCriteria).length === 0
    ) {
      toast.error("Please set up filter criteria");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        project_id: projectId,
        name: formData.name.trim(),
        status: formData.status,
        references: formData.references || null,
        assigned_to:
          formData.assigned_to.length > 0
            ? formData.assigned_to.join(",")
            : null,
        deadline: formData.deadline
          ? new Date(formData.deadline).toISOString()
          : null,
        external_id: formData.external_id || null,
        include_type: formData.include_type,
        description: formData.description || null,
        auto_assign_test_cases: autoAssignTestCases,
      };

      // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
      // if (projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') {
      //   if (baselineId) payload.baseline_id = baselineId;
      //   if (branchId) payload.branch_id = branchId;
      // }

      if (
        projectStructure === "MULTI_SUITE" ||
        projectStructure === "MULTIPLE_TEST_SUITES"
      ) {
        if (suiteIds.length > 0) payload.suite_ids = suiteIds;
      }

      // Add test case selection
      if (formData.include_type === "specific") {
        payload.test_case_ids = selectedTestCases.map((tc) => tc.test_case_id);
      } else if (formData.include_type === "filter") {
        payload.filter_criteria = filterCriteria;
      }

      // Add test plan IDs if available
      if (testPlanIds.length > 0) {
        payload.test_plan_ids = testPlanIds;
      } else if (testPlanId) {
        payload.test_plan_ids = [testPlanId];
      }
      let response;
      if (isEditMode) {
        response = await testrunService.updateTestRun(
          testRunId,
          projectId,
          payload,
        );
      } else {
        response = await testrunService.createTestRun(payload);
      }
      if (response.success) {
        toast.success(
          isEditMode
            ? "Test run updated successfully"
            : "Test run created successfully",
        );
        navigate(
          `/test-runs?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
        );
      } else {
        toast.error(response.message || "Failed to create test run");
      }
    } catch (error) {
      console.error("Error creating test run:", error);
      toast.error(error.response?.data?.message || "Failed to create test run");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(
      `/test-runs?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
    );
  };

  if (!projectId) {
    return (
      <div className="p-6">
        <div className="text-red-600 text-lg font-semibold">
          Project ID is required
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with breadcrumb and action buttons */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            className="h-8 w-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm text-gray-600">
            Test Run /{" "}
            <span className="text-gray-900 font-medium">
              {isEditMode
                ? "Edit Test Run"
                : isCloneMode
                  ? "Clone Test Run"
                  : "New Test Run"}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-red-500 text-red-600 hover:bg-red-50 mr-2"
          >
            Cancel
          </Button>
          <button
            onClick={async () => {
              await handleSave();
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown transition-colors whitespace-nowrap flex-shrink-0"
          >
            <Save className="w-4 h-4 flex-shrink-0" />
            <span>Save</span>
          </button>
        </div>
      </div>

      {/* Form Content */}
      <Card className="p-8 grid-cols-2 px-24">
        <div className="grid grid-cols-1 md:grid-cols-[4fr_1fr] gap-8 items-stretch">
          {/* Left Column */}
          <div className="space-y-6 min-h-full">
            <div className="flex items-center gap-2 flex-row">
              <MuiInput
                id="name"
                label="Test Run Name"
                value={formData.name}
                onChange={(value) => handleChange("name", value)}
                placeholder="Give your Test Run a title..."
                required
              />
              <MuiSelect
                label="Select Existing Test Plan"
                value={testPlanIds}
                onFilterChange={(value) => {
                  if (value && !testPlanIds.includes(value)) {
                    setTestPlanIds([...testPlanIds, value]);
                  }
                }}
                options={testPlans.map((testPlan) => ({
                  label: testPlan.name,
                  value: testPlan.test_plan_id,
                }))}
                className="flex items-center gap-2 mt-2 flex-2"
                sx={{ minWidth: "360px" }}
              />
              <Button
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 flex-1"
                onClick={() => {
                  toast("Create new test plan functionality coming soon");
                }}
              >
                Create New Plan
              </Button>
            </div>

            <div>
              <MuiInput
                id="references"
                label="Reference"
                value={formData.references}
                onChange={(value) => handleChange("references", value)}
                placeholder="Enter Reference of your Test Run..."
              />
            </div>

            <div>
              <div className="mt-3 flex items-center gap-2">
                <MuiSelect
                  label="Assign User"
                  multiple
                  value={formData.assigned_to}
                  onFilterChange={(value) =>
                    handleChange("assigned_to", value)
                  }
                  options={users.map((user) => ({
                    label: user.full_name || user.email,
                    value: String(user.user_id),
                  }))}
                  sx={{ minWidth: 280, flex: 1 }}
                />
                <button
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown-inverted transition-colors whitespace-nowrap flex-shrink-0"
                  onClick={() => {
                    toast("Invite new user functionality coming soon");
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Invite New User
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="auto-assign"
                checked={autoAssignTestCases}
                onChange={(e) => setAutoAssignTestCases(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label
                htmlFor="auto-assign"
                className="text-sm font-normal cursor-pointer"
              >
                Auto-assign all test cases to the test run assignee.
              </Label>
            </div>

            {/* SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out: Baseline/Branch selectors */}
            {/* (projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') && ( ... Baseline * and Branch * UI ... ) */}

            {/* Structure-specific fields for MULTI_SUITE */}
            {(projectStructure === "MULTI_SUITE" ||
              projectStructure === "MULTIPLE_TEST_SUITES") && (
              <div>
                <Label>Suites (Optional)</Label>
                <div className="space-y-2 mt-3 max-h-40 overflow-y-auto border rounded-md p-2">
                  {suites.map((suite) => (
                    <div
                      key={suite.suite_id}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        id={`suite-${suite.suite_id}`}
                        checked={suiteIds.includes(suite.suite_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSuiteIds([...suiteIds, suite.suite_id]);
                          } else {
                            setSuiteIds(
                              suiteIds.filter((id) => id !== suite.suite_id),
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <label
                        htmlFor={`suite-${suite.suite_id}`}
                        className="text-sm cursor-pointer"
                      >
                        {suite.name} ({suite.test_cases_count || 0} test cases)
                      </label>
                    </div>
                  ))}
                  {suites.length === 0 && !loadingSuites && (
                    <p className="text-xs text-muted-foreground">
                      No suites available
                    </p>
                  )}
                  {loadingSuites && (
                    <div className="space-y-2 py-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-[80%]" />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Select one or more suites to include in the test run
                  </p>
                </div>
              </div>
            )}

            {!false && (
              <div>
                <Label className="text-sm font-bold">Add Test Cases *</Label>
                <div className="space-y-5 mt-4">
                  <div className="flex items-start space-x-2">
                    <input
                      type="radio"
                      id="include-all"
                      name="include_type"
                      value="all"
                      checked={formData.include_type === "all"}
                      style={{ accentColor: "#a17561ff" }}
                      onChange={(e) => handleIncludeTypeChange(e.target.value)}
                      className="w-4 h-4 mt-0.5 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <Label
                      htmlFor="include-all"
                      className="text-sm font-bold cursor-pointer flex-1"
                    >
                      Includes all existing test cases and auto-adds new ones
                      until the run is closed.
                    </Label>
                  </div>

                  <div className="flex items-start space-x-2">
                    <input
                      type="radio"
                      id="include-specific"
                      name="include_type"
                      value="specific"
                      checked={formData.include_type === "specific"}
                      onChange={(e) => handleIncludeTypeChange(e.target.value)}
                      style={{ accentColor: "#a17561ff" }}
                      className="w-4 h-4 mt-0.5 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <Label
                      htmlFor="include-specific"
                      className="text-sm font-bold cursor-pointer flex-1"
                    >
                      Manually select test cases; new ones are not auto-added.
                    </Label>
                  </div>

                  <div className="flex items-start space-x-2">
                    <input
                      type="radio"
                      id="include-filter"
                      name="include_type"
                      value="filter"
                      checked={formData.include_type === "filter"}
                      onChange={(e) => handleIncludeTypeChange(e.target.value)}
                      style={{ accentColor: "#a17561ff" }}
                      className="w-4 h-4 mt-0.5 text-purple-600 focus:ring-purple-500 focus:ring-2"
                    />
                    <Label
                      htmlFor="include-filter"
                      className="text-sm font-bold cursor-pointer flex-1"
                    >
                      Dynamic filtering - Auto-includes test cases matching
                      filters while the run is open.
                    </Label>
                  </div>
                </div>

                {(formData.include_type === "specific" ||
                  formData.include_type === "filter") && (
                  <div className="mt-5">
                    <Label>Select Test Cases *</Label>
                    <div className="flex items-center gap-2 mt-3">
                      <MuiInput
                        value={
                          formData.include_type === "specific"
                            ? `${selectedTestCases.length} Test cases Selected`
                            : formData.include_type === "filter"
                              ? `${Object.keys(filterCriteria).length > 0 ? Object.keys(filterCriteria).length : 0} Filter Selected`
                              : "0 Test cases Selected"
                        }
                        readOnly
                        placeholder="0 Test cases Selected"
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                      <button
                        onClick={() => setShowSelectTestCases(true)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown-inverted transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {formData.include_type === "filter"
                          ? "Select Filters"
                          : "Select Test Cases"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-8 flex flex-col min-h-full">
            <div className="flex-1 flex flex-col min-h-0">
              <MuiInput
                multiline
                id="description"
                label="Describe"
                value={formData.description}
                onChange={(value) => handleChange("description", value)}
                placeholder="Describe your Test Run..."
                rows={16}
              />
            </div>
          </div>
        </div>
      </Card>

      <SelectTestCasesModal
        open={showSelectTestCases}
        onClose={() => setShowSelectTestCases(false)}
        projectId={projectId}
        onSave={handleTestCasesSelected}
        selectedTestCases={selectedTestCases}
        mode={formData.include_type === "filter" ? "filter" : "specific"}
      />
    </div>
  );
};

export default NewTestRunPage;
